import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { PermissionKey } from '@charisma/shared';
import { hasPermission as sharedHasPermission } from '@charisma/shared';
import { apiClient } from '@/api/client';

interface CurrentUser {
  id: string;
  email: string;
  permissions: PermissionKey[];
}

interface JwtPayload {
  sub: string;
  email: string;
  permissions: string[];
}

function decodeJwt(token: string): JwtPayload {
  const base64 = (token.split('.')[1] ?? '').replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(atob(base64)) as JwtPayload;
}

const STORAGE_KEY = 'charisma_auth';

interface StoredAuth {
  user: CurrentUser;
  accessToken: string;
  refreshToken: string;
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(null);
  const accessToken = ref<string | null>(null);
  const refreshToken = ref<string | null>(null);

  const isAuthenticated = computed(() => user.value !== null);

  function hasPermission(p: PermissionKey): boolean {
    return user.value ? sharedHasPermission(user.value.permissions, p) : false;
  }

  function setAuth(payload: { user: CurrentUser; accessToken: string; refreshToken?: string }) {
    user.value = payload.user;
    accessToken.value = payload.accessToken;
    if (payload.refreshToken !== undefined) {
      refreshToken.value = payload.refreshToken;
    }
    const stored: StoredAuth = {
      user: payload.user,
      accessToken: payload.accessToken,
      refreshToken: payload.refreshToken ?? refreshToken.value ?? '',
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  }

  function clear() {
    user.value = null;
    accessToken.value = null;
    refreshToken.value = null;
    localStorage.removeItem(STORAGE_KEY);
  }

  function initFromStorage() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const stored = JSON.parse(raw) as StoredAuth;
      user.value = stored.user;
      accessToken.value = stored.accessToken;
      refreshToken.value = stored.refreshToken;
    } catch {
      clear();
    }
  }

  async function login(email: string, password: string) {
    const { data } = await apiClient.post<{ accessToken: string; refreshToken: string }>('/auth/login', {
      email,
      password,
    });
    const payload = decodeJwt(data.accessToken);
    setAuth({
      user: { id: payload.sub, email: payload.email, permissions: payload.permissions as PermissionKey[] },
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
    });
  }

  async function logout() {
    try {
      await apiClient.post('/auth/logout');
    } finally {
      clear();
    }
  }

  return { user, accessToken, refreshToken, isAuthenticated, hasPermission, setAuth, clear, initFromStorage, login, logout };
});
